/*#
# b3d-manipulator

**Translate, rotate and scale handles you can grab — with a mouse, a finger, or
a hand in a headset.** Drop it in a scene, point it at an element or a mesh,
and it writes the result back.

## Demo

```js
import { b3d, b3dSun, b3dSkybox, b3dLight, b3dGround, b3dBox, b3dManipulator, toggle3d, select3d, label3d } from 'tosijs-3d'
import { orbitCam } from 'tosijs-3d/demo-utils'
import { elements } from 'tosijs'

const { div } = elements
const readout = div({ class: 'readout' }, '0, 0.5, 0')

const gizmo = b3dManipulator({
  target: '#thing',
  turn: 'on',
  gridSnap: 0.25,
  angleSnap: 15,
  whenChange: (t) => {
    const n = (v) => Math.round(v * 100) / 100
    readout.textContent =
      `${n(t.position.x)}, ${n(t.position.y)}, ${n(t.position.z)}` +
      `   ·   ${Math.round(t.rotation.ry)}°   ·   ×${n(t.scale.x)}`
  },
})

// The controls live in the SCENE PANEL, so the same demo is usable in a headset.
const panel = () => [
  label3d({ text: 'Manipulator' }),
  toggle3d({
    label: 'move',
    value: gizmo.move !== 'off',
    handleChange: (v) => { gizmo.move = v ? 'on' : 'off' },
  }),
  toggle3d({
    label: 'turn',
    value: gizmo.turn !== 'off',
    handleChange: (v) => { gizmo.turn = v ? 'on' : 'off' },
  }),
  toggle3d({
    label: 'scale',
    value: gizmo.scale !== 'off',
    // Scale is exclusive of the other two — the element enforces that itself,
    // so the toggles simply follow it.
    handleChange: (v) => { gizmo.scale = v ? 'on' : 'off' },
  }),
  select3d({
    label: 'grid',
    value: 0.25,
    options: [{ label: 'off', value: 0 }, 0.25, 0.5, 1],
    handleChange: (v) => { gizmo.gridSnap = Number(v) },
  }),
  select3d({
    label: 'angle',
    value: 15,
    options: [{ label: 'off', value: 0 }, 5, 15, 45, 90],
    handleChange: (v) => { gizmo.angleSnap = Number(v) },
  }),
]

preview.append(
  b3d(
    {
      scenePanel: panel,
      sceneCreated(el) {
        orbitCam(el, { alpha: -1.1, beta: 1.05, radius: 8, target: [0, 0.5, 0] })
      },
    },
    b3dSun({}),
    b3dSkybox({ timeOfDay: 10 }),
    b3dLight({ intensity: 0.5 }),
    b3dGround({ size: 40, color: '#5d6b4a' }),
    b3dBox({ id: 'thing', y: 0.5, color: '#c8963c' }),
    gizmo
  ),
  readout
)
```
```css
.preview { display: flex; flex-direction: column; height: 100%; }
tosi-b3d { flex: 1; min-height: 0; width: 100%; }
.readout {
  padding: 4px 10px;
  border-radius: 6px;
  font-family: ui-monospace, monospace;
  font-size: 13px;
  color: #fff;
  background: #0009;
}
```

Drag the arrows to move, the rings to turn, the cubes to scale. The pads move in
a plane. Everything is on at once — the part you grab says what the drag means.

## Attributes

| attribute | default | |
| --- | --- | --- |
| `target` | `''` | CSS selector for the element to manipulate. Or set `.node` in JS |
| `meshName` | `''` | a mesh in the scene, by name — for things that are not elements |
| `move` | `'on'` | `'on'`/`'off'` — arrows and plane pads |
| `turn` | `'off'` | `'on'`/`'off'` — rings |
| `scale` | `'off'` | `'on'`/`'off'` — cubes, plus the uniform centre cube |
| `gridSnap` | `0` | position snap in metres. `0` is off |
| `angleSnap` | `0` | angle snap in degrees. `0` is off |
| `size` | `0.13` | apparent size — the widget is this fraction of its distance from the camera, so it stays constant on screen |
| `disabled` | `false` | hide the handles and ignore input |

`whenChange(transform)` fires live during a drag; `whenCommit(transform)`
fires once on release, snapped, and only when something actually changed — which
is your undo step. Both also dispatch a DOM `change` / `commit` event carrying
the transform in `detail`. A component's callbacks are `when*`, not `handle*`.

## A grip, not a mode

Every enabled affordance is on screen at once, and the grip you grab says what
the drag means. A mode switch makes you say what you want twice, once to a
toolbar and again to the handle, and costs a round trip for the commonest edit
there is: nudge it over, then turn it a bit. The cost is crowding, and the
answer to that is the transform set: turn off what you are not using and its
grips are not built.

Each grip answers one question: *given where the pointer is aiming now, what
value should this handle have?*

| grip | reading |
| --- | --- |
| `translate` | the point on the axis closest to the pointer's ray |
| `planar` | where the ray crosses the plane the grip lies in, as a point |
| `rotate` | the angle around the axis where the ray crosses its plane |
| `scale` | the same projection as translate, read as a ratio |
| `uniform` | the ray's distance from the widget centre, read as a ratio |

## Scale is exclusive of move and turn

`node.scaling` is local, so scale grips ride the object's own axes while move
rides the world's. A widget showing both draws two frames at once and can only
mislead. Turning `scale` on therefore turns `move` and `turn` off, and vice versa —
the element does this to itself rather than letting you build the misleading
combination.

## Snapping and the frozen frame

Snapping quantises the resulting VALUE, not each step of the movement, so a
snapped object is always exactly on the grid however long the drag. Rotation is
the exception: it snaps its DELTA, because a 15° step about the ring you grabbed
is what the setting promises, even from a start rotation that is not on the grid.

A drag measures against the object's axes as they were when it began. Reading
them live is a feedback loop for rotation: each sample would be measured in a
frame your own output just turned, and a tiny movement would spin the object
hundreds of degrees.

## Where the transform is written, and why it is a fork

Getting this wrong does not error. It silently does nothing, which is the worst
failure mode a manipulator can have.

| target | written to | why |
| --- | --- | --- |
| an ELEMENT (`b3d-prop`, `b3d-destroyable`, any `AbstractMesh`) | `el.x/y/z`, `el.rx/ry/rz` | the element OWNS its transform — `render()` writes `mesh.position` and `rotationQuaternion` from those attributes every render, so a write straight to the mesh is undone the next time anything re-renders |
| a bare NODE | `node.position`, `node.rotationQuaternion`, `node.scaling` | nothing manages it, so the node IS the truth |

**Scale goes to the node in both branches.** `AbstractMesh.render()` does not
sync scaling, and no element attribute covers per-axis scale, so the node is
where a scale survives.

**Rotating a node clears its quaternion first.** A `TransformNode` ignores
`.rotation` while it has a `rotationQuaternion`, and the glTF loader always
sets one. Writing `.rotation` alone would leave every loaded model at its baked
orientation while `position` worked, which looks wired up and is not.

## Sized for hands, picked in two passes

Every drawn handle carries a second, invisible, fatter mesh that exists only to
be picked, because a gizmo that is visually thin is impossible to grab with a
controller at arm's length or a finger on a phone. A hand inside a handle's
`nearRadius` grabs it directly; anything further grabs by pointing.

Picking tries the DRAWN handles first, then the fat targets. The fat targets
overlap by design, so a single pass over both would let ray depth decide, and a
ring's tube passing in front of the arrowhead under the cursor would win.

Handles draw on top (rendering group 1), because the object you most want to
move is often embedded in something else. Rendering group has no bearing on
picking; it is safe here only because the handle pick admits nothing but handle
meshes, so an occluder is never a candidate.

## Driving it from XR, or from anything else

The flat pointer is wired for you. Everything else goes through three methods
that take world rays, so a controller, a hand or a script drives the same code:

```javascript
const grabbed = el.grab({ origin, direction }, { secondary: false })
el.drag({ origin, direction })
el.release()
```

`grabNear({x, y, z})` is the near-interaction form: a hand inside a handle grabs
it directly, which beats whatever the same controller's ray happens to be
crossing further away.

The pure maths (`beginDrag`, `updateDrag`, `commitTransform`, `snap`, …) and the
handle geometry (`createHandles`, `composeRotation`, `axisFrameOf`) are exported
too, for a consumer building its own widget.
*/
/*{ "parent": "interacting", "order": 60 }*/

import { handlerOf } from './handler-of.js'
import * as BABYLON from '@babylonjs/core'
import { B3dChild, cameraIsAttached, isOff } from './b3d-utils.js'
import {
  beginDrag,
  commitTransform,
  dragChanged,
  updateDrag,
  type Drag,
  type Grip,
  type ManipulatorRay,
  type ManipulatorTransform,
  type TransformSet,
  type Vec3,
} from './manipulator.js'
import {
  axisFrameOf,
  composeRotation,
  createHandles,
  type HandlesView,
} from './manipulator-view.js'
import type { B3d } from './tosi-b3d.js'

const DEG_TO_RAD = Math.PI / 180

/** The element shape we write a transform to, when the target is one. */
interface TransformableElement extends HTMLElement {
  x?: number
  y?: number
  z?: number
  rx?: number
  ry?: number
  rz?: number
  mesh?: BABYLON.TransformNode | null
}

export class B3dManipulator extends B3dChild {
  static preferredTagName = 'tosi-b3d-manipulator'

  static initAttributes = {
    target: '',
    meshName: '',
    /*
    `move`/`turn`, NOT `translate`/`rotate`.

    `translate` is a real HTMLElement property — the global HTML attribute that
    tells the browser's translation machinery whether to translate an element's
    text. Declaring an attribute by that name gives one word two owners, and
    the compiler catches it only because the base type disagrees. `move` and
    `turn` are also the words an author would use.

    'on'/'off' rather than booleans, because a boolean attribute cannot default
    to true and `move` genuinely should.
    */
    move: 'on',
    turn: 'off',
    scale: 'off',
    gridSnap: 0,
    angleSnap: 0,
    size: 0.13,
    disabled: false,
  }

  declare target: string
  declare meshName: string
  declare move: string
  declare turn: string
  declare scale: string
  declare gridSnap: number
  declare angleSnap: number
  declare size: number
  declare disabled: boolean

  /** Live during a drag; fires again for every frame the pointer moves. */
  whenChange: ((t: ManipulatorTransform) => void) | null = null
  /** Once on release, snapped, and only when something changed. */
  whenCommit: ((t: ManipulatorTransform) => void) | null = null

  /** The node being manipulated. Set this directly to skip `target`. */
  node: BABYLON.TransformNode | null = null

  private _view: HandlesView | null = null
  private _drag: Drag | null = null
  private _pointer: BABYLON.Nullable<BABYLON.Observer<BABYLON.PointerInfo>> =
    null
  private _frame: BABYLON.Nullable<BABYLON.Observer<BABYLON.Scene>> = null
  private _cameraWasAttached = false

  sceneReady(owner: B3d, scene: BABYLON.Scene): void {
    this._view = createHandles(scene)
    this._view.setTransforms(this._transformSet())

    this._frame = scene.onBeforeRenderObservable.add(() => this._track())
    this._pointer = scene.onPointerObservable.add((info) => {
      this._onPointer(info, scene)
    })
    void owner
  }

  sceneDispose(): void {
    const scene = this.owner?.scene
    if (scene != null) {
      scene.onBeforeRenderObservable.remove(this._frame)
      scene.onPointerObservable.remove(this._pointer)
    }
    this._frame = null
    this._pointer = null
    this._releaseCamera()
    this._view?.dispose()
    this._view = null
    this._drag = null
    super.sceneDispose()
  }

  render(): void {
    super.render()
    this._view?.setTransforms(this._transformSet())
  }

  /*
  SCALE IS EXCLUSIVE OF MOVE AND TURN.

  Not a preference — `node.scaling` is local, so scale grips ride the object's
  axes while move rides the world's, and a widget drawing both frames at once
  can only mislead. Resolved here rather than trusted from the attributes, so
  the misleading combination is not expressible.
  */
  private _transformSet(): TransformSet {
    const scale = !isOff(this.scale)
    return {
      translate: !scale && !isOff(this.move),
      rotate: !scale && !isOff(this.turn),
      scale,
    }
  }

  /** The element whose transform we write, if the target is one. */
  private _targetElement(): TransformableElement | null {
    const selector = String(this.target ?? '')
    if (selector === '' || this.owner == null) return null
    const found =
      this.owner.querySelector(selector) ??
      (this.getRootNode() as ParentNode).querySelector?.(selector) ??
      null
    return (found as TransformableElement) ?? null
  }

  /** The node we move, from whichever route was used to name it. */
  private _targetNode(): BABYLON.TransformNode | null {
    if (this.node != null) return this.node
    const el = this._targetElement()
    if (el?.mesh != null) return el.mesh
    const name = String(this.meshName ?? '')
    if (name !== '' && this.owner != null) {
      return this.owner.scene.getNodeByName(
        name
      ) as BABYLON.TransformNode | null
    }
    return null
  }

  /** Where the widget sits and how big it draws, once per frame. */
  private _track(): void {
    const view = this._view
    const scene = this.owner?.scene
    if (view == null || scene == null) return
    const node = this._targetNode()
    const on = node != null && this.disabled !== true
    view.setVisible(on)
    if (!on) return

    /*
    THE WIDGET HOLDS STILL WHILE YOU DRAG IT.

    It used to re-read the target's world position every frame, which is right
    for an object sitting still and wrong for one that is ALIVE: a weapon
    parented to a hand bone inherits the idle animation, so the handles drifted
    and breathed under the cursor mid-drag. Tonio: "the way the manipulator
    moves around as I drag it is a bit odd (it doesn't affect the drag)."

    It did not affect the drag — the solve is against pointer rays and the
    target's own transform, not against where the handles are drawn — so this is
    purely about it feeling stable in the hand. Freezing the anchor for the
    duration of a grab is what every DCC gizmo does, and for the same reason.
    */
    /*
    ...BUT IT FOLLOWS THE MOVE YOU ARE MAKING. Frozen outright, the handles
    were left behind for the whole of a move drag and only caught up on
    release. So the frozen anchor moves by the change in the target's OWN
    position (its local position, carried into the world by its parent), which
    is the drag's doing; the parent's animation, the breathing, is still left out.
    */
    let p: BABYLON.Vector3
    if (this._drag != null && this._anchor != null) {
      if (this._dragFrom == null) this._dragFrom = node.position.clone()
      const moved = node.position.subtract(this._dragFrom)
      const parent = node.parent as BABYLON.TransformNode | null
      if (parent != null) {
        parent.computeWorldMatrix(true)
        BABYLON.Vector3.TransformNormalToRef(
          moved,
          parent.getWorldMatrix(),
          moved
        )
      }
      p = this._anchor.add(moved)
    } else {
      this._dragFrom = null
      p = node.getAbsolutePosition()
      this._anchor = p.clone()
      view.setOrientation(this._rotationOf(node))
    }
    view.moveTo({ x: p.x, y: p.y, z: p.z })

    const camera = scene.activeCamera
    if (camera != null) {
      // Constant size ON SCREEN: world-sized handles are correct at exactly one
      // camera distance and unusable at every other.
      const d = BABYLON.Vector3.Distance(camera.globalPosition, p)
      view.setScale(Math.max(0.05, d * (Number(this.size) || 0.13)))
    }
  }

  private _rotationOf(node: BABYLON.TransformNode): {
    rx: number
    ry: number
    rz: number
  } {
    const el = this._targetElement()
    if (el != null && typeof el.ry === 'number') {
      return { rx: el.rx ?? 0, ry: el.ry ?? 0, rz: el.rz ?? 0 }
    }
    const e =
      node.rotationQuaternion != null
        ? node.rotationQuaternion.toEulerAngles()
        : node.rotation
    return { rx: e.x / DEG_TO_RAD, ry: e.y / DEG_TO_RAD, rz: e.z / DEG_TO_RAD }
  }

  /**
   * A world point from the node's PARENT frame — the inverse of
   * `_toParentSpace`.
   */
  private _toWorldSpace(
    node: BABYLON.TransformNode,
    p: { x: number; y: number; z: number }
  ): BABYLON.Vector3 {
    const local = new BABYLON.Vector3(p.x, p.y, p.z)
    const parent = node.parent as BABYLON.TransformNode | null
    if (parent == null || typeof parent.getWorldMatrix !== 'function') {
      return local
    }
    parent.computeWorldMatrix(true)
    return BABYLON.Vector3.TransformCoordinates(local, parent.getWorldMatrix())
  }

  /** The transform as it stands now, in the units the drag speaks. */
  private _currentTransform(node: BABYLON.TransformNode): ManipulatorTransform {
    /*
    THE ELEMENT'S OWN `x`/`y`/`z` ARE THE TRUTH, not the mesh's origin.

    They are not always the same point. `b3d-launcher` defines them as the GRIP —
    where the hand holds the weapon — and offsets the model so the grip lands
    there, so the mesh origin sits a grip-length away. Reading the origin and
    writing it back to `x`/`y`/`z` is then not an identity: a click with no drag
    at all moved the weapon by the length of its own grip, which is how this was
    found (measured: 0,0,0 became 0.007, 0.099, -0.037 on a no-op write).

    Going through the element's own numbers makes the round trip exact whatever
    the element means by them, and puts the gizmo on the handle the author
    declared rather than on wherever the modeller happened to put the origin.
    */
    const el = this._targetElement()
    const p =
      el != null && typeof el.x === 'number'
        ? this._toWorldSpace(node, { x: el.x, y: el.y ?? 0, z: el.z ?? 0 })
        : node.getAbsolutePosition()
    return {
      position: { x: p.x, y: p.y, z: p.z },
      rotation: this._rotationOf(node),
      scale: {
        x: node.scaling.x,
        y: node.scaling.y,
        z: node.scaling.z,
      },
    }
  }

  /* --------------------------------------------------------------------- *
   * Grabbing — a ray API, so XR and the flat pointer are the same code
   * --------------------------------------------------------------------- */

  /** Start a drag from a world ray. Returns whether a handle was grabbed. */
  grab(ray: ManipulatorRay, options: { secondary?: boolean } = {}): boolean {
    const view = this._view
    const node = this._targetNode()
    if (view == null || node == null || this.disabled === true) return false
    const grip = view.gripAt(ray)
    return grip != null ? this._begin(grip, node, ray, options) : false
  }

  /**
   * Start a drag from a HAND inside a handle.
   *
   * Near beats far: a hand inside a handle is unambiguous, and beats whatever
   * the same controller's ray happens to be crossing further away.
   */
  grabNear(
    hand: Vec3,
    ray: ManipulatorRay,
    options: { secondary?: boolean } = {}
  ): boolean {
    const view = this._view
    const node = this._targetNode()
    if (view == null || node == null || this.disabled === true) return false
    const grip = view.nearestGrip(hand)
    return grip != null ? this._begin(grip, node, ray, options) : false
  }

  private _begin(
    grip: Grip,
    node: BABYLON.TransformNode,
    ray: ManipulatorRay,
    options: { secondary?: boolean }
  ): boolean {
    const transform = this._currentTransform(node)
    const drag = beginDrag(
      grip,
      transform.position,
      transform,
      ray,
      axisFrameOf(transform.rotation),
      { secondary: options.secondary === true }
    )
    if (drag == null) return false
    this._drag = drag
    // The camera must stop listening the moment a handle is grabbed, or the
    // drag moves the object AND orbits the view under it.
    this._captureCamera()
    return true
  }

  /** Continue a drag. Harmless when nothing is grabbed. */
  drag(ray: ManipulatorRay): void {
    const d = this._drag
    if (d == null) return
    const options = {
      gridSnap: Number(this.gridSnap) || 0,
      angleSnap: Number(this.angleSnap) || 0,
    }
    if (!updateDrag(d, ray, composeRotation, options)) return
    this._write(d.current)
    handlerOf<(t: ManipulatorTransform) => void>(
      this as unknown as Record<string, unknown>,
      'whenChange',
      'handleChange',
      '<tosi-b3d-manipulator>'
    )?.(d.current)
    this.dispatchEvent(
      new CustomEvent('change', { detail: d.current, bubbles: true })
    )
  }

  /**
   * Finish a drag, committing the snapped transform.
   *
   * Returns whether the gesture was a DRAG at all. A press that grabbed a
   * handle and never moved is a CLICK, and the caller may want to treat it as
   * one: with everything switched on the widget covers a good deal of what is
   * behind it, so once something is selected, tapping beside it usually lands
   * on a handle instead.
   */
  release(): boolean {
    const d = this._drag
    this._drag = null
    // Always give the camera back, even on a drag that grabbed nothing —
    // otherwise a mis-click leaves the view frozen with no way to recover.
    this._releaseCamera()
    if (d == null) return false
    const options = {
      gridSnap: Number(this.gridSnap) || 0,
      angleSnap: Number(this.angleSnap) || 0,
    }
    const committed = commitTransform(d, options)
    if (!dragChanged(d, committed)) return d.moved
    this._write(committed)
    handlerOf<(t: ManipulatorTransform) => void>(
      this as unknown as Record<string, unknown>,
      'whenCommit',
      'handleCommit',
      '<tosi-b3d-manipulator>'
    )?.(committed)
    this.dispatchEvent(
      new CustomEvent('commit', { detail: committed, bubbles: true })
    )
    return true
  }

  /** Where the handles were when the grab started — see `_track`. */
  private _anchor: BABYLON.Vector3 | null = null
  /** The target's local position when the current drag began. */
  private _dragFrom: BABYLON.Vector3 | null = null

  /** Is a drag in progress? */
  get dragging(): boolean {
    return this._drag != null
  }

  /* --------------------------------------------------------------------- *
   * Writing
   * --------------------------------------------------------------------- */

  /**
   * A world point in the node's PARENT frame — or unchanged if it has none.
   *
   * `TransformCoordinates` through the inverse world matrix, rather than
   * subtracting the parent's position, because a parent can be rotated and
   * scaled (a hand bone is all three) and subtraction only handles the easy
   * case — which is exactly the trap this function exists to close.
   */
  private _toParentSpace(
    node: BABYLON.TransformNode,
    p: { x: number; y: number; z: number }
  ): BABYLON.Vector3 {
    const world = new BABYLON.Vector3(p.x, p.y, p.z)
    const parent = node.parent as BABYLON.TransformNode | null
    if (parent == null || typeof parent.getWorldMatrix !== 'function') {
      return world
    }
    parent.computeWorldMatrix(true)
    return BABYLON.Vector3.TransformCoordinates(
      world,
      BABYLON.Matrix.Invert(parent.getWorldMatrix())
    )
  }

  private _write(t: ManipulatorTransform): void {
    const node = this._targetNode()
    if (node == null) return
    const el = this._targetElement()

    if (el != null && typeof el.x === 'number') {
      // The element owns position and rotation: writing the mesh instead is
      // undone by its next render, silently.
      /*
      ⚠️ INTO THE PARENT'S FRAME FIRST.

      The drag works in WORLD space — it must, because it is solving against
      pointer rays — and `_currentTransform` reads `getAbsolutePosition()`. But
      `el.x/y/z` are the element's LOCAL offset from whatever it hangs on. Those
      are the same numbers only for an unparented node, which is every target
      this widget had been tried against, so the bug sat in plain sight.

      Parent it to anything and the first click teleports the target by the
      parent's world offset, and the axes it drags along are the parent's
      rotated ones rather than the world ones you can see. Reported against a
      pistol parented to a hand bone: "the axes aren't wired up correctly and as
      soon as I clicked the gizmo the gun jumped away." Both halves, one cause.
      */
      const local = this._toParentSpace(node, t.position)
      el.x = local.x
      el.y = local.y
      el.z = local.z
      el.rx = t.rotation.rx
      el.ry = t.rotation.ry
      el.rz = t.rotation.rz
    } else {
      // Same conversion for a bare node: `position` is parent-relative too.
      const local = this._toParentSpace(node, t.position)
      node.position.copyFrom(local)
      // Clear the quaternion or `.rotation` is ignored — the glTF loader always
      // sets one, and a rotation drag would move nothing with no error.
      node.rotationQuaternion = BABYLON.Quaternion.RotationYawPitchRoll(
        t.rotation.ry * DEG_TO_RAD,
        t.rotation.rx * DEG_TO_RAD,
        t.rotation.rz * DEG_TO_RAD
      )
    }
    // Scale goes to the NODE either way: `AbstractMesh.render()` does not sync
    // scaling, and no element attribute covers per-axis scale.
    node.scaling.set(t.scale.x, t.scale.y, t.scale.z)
  }

  /* --------------------------------------------------------------------- *
   * The flat pointer
   * --------------------------------------------------------------------- */

  private _onPointer(info: BABYLON.PointerInfo, scene: BABYLON.Scene): void {
    if (this.disabled === true) return
    const camera = scene.activeCamera
    if (camera == null) return
    const e = info.event as PointerEvent
    const pick = scene.createPickingRay(
      scene.pointerX,
      scene.pointerY,
      BABYLON.Matrix.Identity(),
      camera
    )
    const ray: ManipulatorRay = {
      origin: { x: pick.origin.x, y: pick.origin.y, z: pick.origin.z },
      direction: {
        x: pick.direction.x,
        y: pick.direction.y,
        z: pick.direction.z,
      },
    }

    if (info.type === BABYLON.PointerEventTypes.POINTERDOWN) {
      // `button === 2` is the secondary: on a scale grip it inverts which axes
      // move. Latched at the grab — see `DragOptions.secondary`.
      this.grab(ray, { secondary: e.button === 2 })
      return
    }
    if (info.type === BABYLON.PointerEventTypes.POINTERMOVE) {
      this.drag(ray)
      return
    }
    if (
      info.type === BABYLON.PointerEventTypes.POINTERUP ||
      info.type === BABYLON.PointerEventTypes.POINTERDOUBLETAP
    ) {
      this.release()
    }
  }

  private _captureCamera(): void {
    const camera = this.owner?.scene.activeCamera
    if (camera == null) return
    this._cameraWasAttached = cameraIsAttached(camera)
    if (this._cameraWasAttached) camera.detachControl()
  }

  private _releaseCamera(): void {
    if (!this._cameraWasAttached) return
    this._cameraWasAttached = false
    const camera = this.owner?.scene.activeCamera
    const canvas = this.owner?.scene.getEngine().getRenderingCanvas()
    if (camera != null && canvas != null) camera.attachControl(canvas, true)
  }
}

export const b3dManipulator = B3dManipulator.elementCreator()
